import { route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { API_BASE_URL } from '../../config.js';
import { useAuth } from '../../context/AuthContext.js';
import { ApiError, api } from '../../services/api.js';
import { resolvePostSignInDestination } from '../../services/signInReturnPath.js';

interface LoginPageProps {
  path?: string;
}

export function LoginPage(_props: LoginPageProps) {
  const { login, isAuthenticated } = useAuth();

  useEffect(() => {
    // 079 D3: this effect fires when `isAuthenticated` flips — which happens inside `login()` on
    // line 28, before the submit handler's own navigation runs — so both arms navigate and the last
    // one wins. The race is not won here; it is made irrelevant, because both arms ask the same
    // resolver and get the same answer (FR-011). `replace` is kept: the sign-in screen must not sit
    // in history behind the destination.
    if (isAuthenticated) route(resolvePostSignInDestination(), true);
  }, [isAuthenticated]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');

  async function handleSubmit(e: Event) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUnverifiedEmail(false);
    setResendSent(false);
    setResendError('');
    try {
      await login(email, password);
      route(resolvePostSignInDestination());
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Login failed');
      setUnverifiedEmail(err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED');
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    setResendError('');
    setResendSent(false);
    try {
      await api.post('/auth/resend-verification', { email });
      setResendSent(true);
    } catch {
      setResendError('Could not send the verification email. Please try again.');
    }
  }

  function handleGoogleLogin() {
    window.location.href = `${API_BASE_URL}/auth/google`;
  }

  return (
    <div className="page-fill bg-bg flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-3xl font-bold text-text mb-2">
          CARii <span className="text-primary">Kereta</span>
        </h1>
        <p className="text-gray-400 mb-8">Sign in to your account</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="login-email" className="text-sm text-gray-400">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              required
              className="bg-surface-mid text-text rounded-lg px-4 py-3"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="login-password" className="text-sm text-gray-400">
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                {...(showPassword ? { type: 'text' as const } : { type: 'password' as const })}
                required
                className="bg-surface-mid text-text rounded-lg px-4 py-3 w-full pr-11"
                value={password}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          {unverifiedEmail &&
            (resendSent ? (
              <p className="text-sm text-gray-400">Check your email for a new link.</p>
            ) : (
              <>
                {resendError && <p className="text-sm text-red-400">{resendError}</p>}
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="text-left text-sm text-primary hover:underline"
                >
                  Resend verification email
                </button>
              </>
            ))}
          <Button type="submit" loading={loading} size="lg" className="w-full">
            Sign In
          </Button>
        </form>

        <div className="my-4 flex items-center gap-3">
          <div className="flex-1 h-px bg-surface-mid" />
          <span className="text-xs text-gray-500">or</span>
          <div className="flex-1 h-px bg-surface-mid" />
        </div>

        <Button variant="secondary" size="lg" className="w-full" onClick={handleGoogleLogin}>
          Continue with Google
        </Button>

        <div className="mt-6 text-center">
          <a href="/account/register" className="text-sm text-primary hover:underline">
            Create account
          </a>
          {' · '}
          <a href="/account/forgot-password" className="text-sm text-gray-400 hover:underline">
            Forgot password?
          </a>
        </div>
      </div>
    </div>
  );
}
